import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';


import {ChunkedDownloadTool} from 'src/components/chuncked-download-tool';
import {PLATFORM_COLUMN_ID, projectPlatformColumnIds} from 'src/components/user-platform-column/platform-columns';
import {usePlatformColumn} from 'src/components/user-platform-column/use-platform-column';
import {getProductAccessValue, isProductColumn, visibleColumnIds} from 'src/components/user-product-columns/product-columns';
import {useProductColumns} from 'src/components/user-product-columns/product-columns-context';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {useNewsletterDictionary} from 'src/hooks/dictionaries/useNewsletterDictionary';
import {getUsersAudit} from 'src/openapi/admin/apis/AuditControllerApi';
import {getAccountName} from 'src/openapi/admin/apis/GetAccountNameApi';
import {getGroupName} from 'src/openapi/admin/apis/GetGroupNameApi';
import {getUsersByIds} from 'src/openapi/admin/apis/GetUsersApi';
import {getUsersCRMIDsByIds} from 'src/openapi/admin/apis/GetUsersCrmIdsApi';
import {getUsersNewsletters} from 'src/openapi/admin/apis/GetUsersNewslettersApi';
import {searchUsersActiveProducts} from 'src/openapi/admin/apis/ProductsApi';
import {getBatchUserSubscriptions} from 'src/openapi/admin/apis/UserLcdSubscriptionsApi';
import {
    getUsersActivities,
    getUsersSavedSearches,
} from 'src/openapi/admin/apis/UserSearchApi';
import {getUsersTotalLimits} from 'src/openapi/admin/apis/UserSearchLimitApi';
import {useAppDispatch} from 'src/store/hooks';
import {getSuccessPayload} from 'src/utils/getSuccessPayload';

import {FormattedDate} from '@pb/kit-component-formatters';

import {COLUMNS, HEADERS, TOTAL_LIMIT_COLUMNS, USED_LIMIT_COLUMNS, UserActivitySource} from '../../constants';
import {useGetColumnValue} from '../../hooks/useGetColumnValue';
import {selectSelectedColumns} from '../../slices/user-search-result-columns.slice';
import {userSearchResultStateSelector} from '../../slices/user-search-result-state.slice';
import {userSearchResultResultSelector} from '../../slices/user-search-result.slice';
import {getMappedLimitColumnId} from '../../utils';

import {USER_DOWNLOAD_FILE_NAME, USER_DOWNLOAD_TASK_NAME} from './constants';


import type {GetData} from 'src/components/chuncked-download-tool/typedef';
import type {Error} from 'src/openapi/admin/models/Error';
import type {UserSearchLimit} from 'src/openapi/admin/models/UserSearchLimit';

type Props = {
    userIds: number[],
    children: (handler: () => void) => React.ReactNode,
    getSortedEntityIds: () => Promise<{
        type: string,
        payload: {
            data: number[] | string[],
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
};

export const UserSearchDownloadTool = ({userIds, children, getSortedEntityIds}: Props) => {
    const apiDispatch = useAppDispatch();
    const templateColumns = useSelector(selectSelectedColumns);
    const {products, headers: productHeaders, ready: productsReady} = useProductColumns();
    const {enabled} = usePlatformColumn();
    const selectedColumns = useMemo(
        () => visibleColumnIds(projectPlatformColumnIds(templateColumns, enabled), products),
        [templateColumns, products, enabled],
    );
    const cacheKeys = useSelector(userSearchResultStateSelector);
    const result = useSelector(userSearchResultResultSelector);

    const {getColumnValue} = useGetColumnValue();
    const {newsletterLabels} = useNewsletterDictionary();

    const fileHeaders = useMemo(() => {
        return selectedColumns.reduce((acc, curr) => ({...acc, [curr]: curr === PLATFORM_COLUMN_ID ? 'Platform' : productHeaders[curr] || HEADERS[curr]}), {});
    }, [selectedColumns, productHeaders]);

    const getData: GetData<Record<string, string>> = useCallback(async (selectedIds: number[]) => {
        if (!productsReady && templateColumns.some(isProductColumn)) {
            return {type: '', payload: {
                success: false,
                error: true,
                data: {message: 'Load the product catalogue before downloading product columns'},
            }};
        }
        let data: Record<string, string>[] = [];
        let fetchingFailed = false;

        let ids: number[] = [];

        if (result && result.totalCount < MAX_SELECTED_ENTITIES) {
            const sortedEntityIdsResponse = await getSortedEntityIds();
            if (sortedEntityIdsResponse.payload.success) {
                const sortedEntityIds = sortedEntityIdsResponse.payload.data as number[];
                ids = sortedEntityIds.filter(id => selectedIds.includes(id));
            } else {
                fetchingFailed = true;
            }
        } else {
            ids = selectedIds;
        }

        const baseResults = await apiDispatch(getUsersByIds({requestBody: ids}));
        if (baseResults.payload.success) {
            const baseResultsResponse = baseResults.payload.data;

            const users = ids.map(id => baseResultsResponse[id]);
            const emailsToIdsMap = users.reduce<Record<string, number>>((acc, curr) => {
                acc[curr.email] = curr.id;
                return acc;
            }, {});

            data = users.map(user => selectedColumns.reduce<Record<string, string>>((acc, curr) => {
                const value = getColumnValue(curr, user);
                acc[curr] = value === undefined || value === null ? '' : String(value);
                return acc;
            }, {}));

            const productColumnIds = selectedColumns.filter(isProductColumn);
            if (productColumnIds.length && ids.length) {
                const response = await apiDispatch(searchUsersActiveProducts({
                    usersActiveProductsSearchRequest: {userIds: ids},
                }));
                if (response.payload.success) {
                    const access = response.payload.data;
                    data = data.map((row, index) => ({
                        ...row,
                        ...Object.fromEntries(productColumnIds.map(column => [
                            column, getProductAccessValue(access, users[index].id, column),
                        ])),
                    }));
                } else {
                    fetchingFailed = true;
                }
            }

            if (selectedColumns.includes(COLUMNS.ACCOUNT_ID)) {
                const accountIds = users.map(user => user.accountId);
                const accountNamesResponse = await apiDispatch(getAccountName({
                    requestBody: [...new Set(accountIds)],
                }));

                const accountNames = getSuccessPayload(accountNamesResponse.payload);
                if (accountNames) {
                    data = data.map(row => {
                        const accountId = row[COLUMNS.ACCOUNT_ID];
                        return {
                            ...row,
                            [COLUMNS.ACCOUNT_ID]: accountId ? accountNames[accountId] ?? '' : '',
                        };
                    });
                } else {
                    fetchingFailed = true;
                }
            }

            if (selectedColumns.includes(COLUMNS.GROUP_ID)) {
                const groupIds = users.map(user => user.groupId).filter(Boolean) as Array<number | string>;
                const groupNamesResponse = await apiDispatch(getGroupName({
                    requestBody: [...new Set(groupIds)].map(id => Number(id)),
                }));

                const groupNames = getSuccessPayload(groupNamesResponse.payload);
                if (groupNames) {
                    data = data.map(row => {
                        const groupId = row[COLUMNS.GROUP_ID];
                        return {
                            ...row,
                            [COLUMNS.GROUP_ID]: groupId ? groupNames[groupId] ?? '' : '',
                        };
                    });
                } else {
                    fetchingFailed = true;
                }
            }

            if (selectedColumns.includes(COLUMNS.NEWSLETTERS)) {
                const newsletterResponse = await apiDispatch(getUsersNewsletters({
                    getNewslettersRequest: {userIds, sortDirection: cacheKeys.sortCriteria.direction || 'ASC'},
                }));
                const newsletters = getSuccessPayload(newsletterResponse.payload);
                if (newsletters) {
                    data = data.map(row => {
                        const email = row[COLUMNS.EMAIL];
                        const userId = email ? emailsToIdsMap[email] : undefined;
                        const userNewsletters = userId !== undefined ? newsletters[String(userId)] : undefined;

                        return {
                            ...row,
                            [COLUMNS.NEWSLETTERS]: userNewsletters && userNewsletters.length
                                ? userNewsletters.map(code => newsletterLabels[code]).filter(Boolean).join(', ')
                                : 'No Newsletter',
                        };
                    });
                } else {
                    fetchingFailed = true;
                }
            }

            const hasLcdColumn = selectedColumns.includes(COLUMNS.LCD_SUBSCRIPTION_NEWS)
                || selectedColumns.includes(COLUMNS.LCD_SUBSCRIPTION_RESEARCH);
            if (hasLcdColumn) {
                const lcdResponse = await apiDispatch(getBatchUserSubscriptions({
                    getLcdSubscriptionsRequest: {userIds: ids},
                }));
                const lcdData = getSuccessPayload(lcdResponse.payload);
                if (lcdData) {
                    data = data.map(row => {
                        const email = row[COLUMNS.EMAIL];
                        const userId = email ? emailsToIdsMap[email] : undefined;
                        const userLcdData = userId !== undefined ? lcdData[String(userId)] : undefined;
                        const update: Record<string, string> = {};
                        if (selectedColumns.includes(COLUMNS.LCD_SUBSCRIPTION_NEWS)) {
                            const newsRegions = userLcdData?.NEWS || [];
                            const isNewsSubscribed = newsRegions.some(r => r.subscriptions.length > 0);
                            update[COLUMNS.LCD_SUBSCRIPTION_NEWS] = isNewsSubscribed
                                ? 'Subscribed' : 'Not Subscribed';
                        }
                        if (selectedColumns.includes(COLUMNS.LCD_SUBSCRIPTION_RESEARCH)) {
                            const researchRegions = userLcdData?.RESEARCH || [];
                            const isResearchSubscribed = researchRegions.some(r => r.subscriptions.length > 0);
                            update[COLUMNS.LCD_SUBSCRIPTION_RESEARCH] = isResearchSubscribed
                                ? 'Subscribed' : 'Not Subscribed';
                        }
                        return {...row, ...update};
                    });
                } else {
                    fetchingFailed = true;
                }
            }

            if ([
                COLUMNS.LAST_PLATFORM_ACTIVITY,
                COLUMNS.LAST_MOBILE_ACTIVITY,
            ].some(column => selectedColumns.includes(column))) {
                const lastPlatformActivityResponse = await apiDispatch(getUsersActivities({
                    requestBody: userIds,
                }));

                const lastActivity = getSuccessPayload(lastPlatformActivityResponse.payload);

                if (lastActivity) {
                    if (selectedColumns.includes(COLUMNS.LAST_PLATFORM_ACTIVITY)) {
                        const lastPlatformActivity = lastActivity[UserActivitySource.PLATFORM];

                        if (lastPlatformActivity) {
                            data = data.map(row => {
                                const email = row[COLUMNS.EMAIL];
                                const userId = email ? emailsToIdsMap[email] : undefined;

                                return {
                                    ...row,
                                    [`${COLUMNS.LAST_PLATFORM_ACTIVITY}`]: FormattedDate.format({
                                        value: userId !== undefined ? lastPlatformActivity[String(userId)] : undefined,
                                        format: 'YYYY-MM-DD',
                                    }) || '',
                                };
                            });
                        }
                    }

                    if (selectedColumns.includes(COLUMNS.LAST_MOBILE_ACTIVITY)) {
                        const lastMobileActivity = lastActivity[UserActivitySource.MOBILE];
                        if (lastMobileActivity) {
                            data = data.map(row => {
                                const email = row[COLUMNS.EMAIL];
                                const userId = email ? emailsToIdsMap[email] : undefined;

                                return {
                                    ...row,
                                    [`${COLUMNS.LAST_MOBILE_ACTIVITY}`]: FormattedDate.format({
                                        value: userId !== undefined ? lastMobileActivity[String(userId)] : undefined,
                                        format: 'YYYY-MM-DD',
                                    }) || '',
                                };
                            });
                        }
                    }
                } else {
                    fetchingFailed = true;
                }
            }

            if (selectedColumns.includes(COLUMNS.SAVED_SEARCHES)) {
                const savedSearchesResponse = await apiDispatch(getUsersSavedSearches({
                    requestBody: userIds,
                }));
                const savedSearches = getSuccessPayload(savedSearchesResponse.payload);
                if (savedSearches) {
                    data = data.map(row => {
                        const email = row[COLUMNS.EMAIL];
                        const userId = email ? emailsToIdsMap[email] : undefined;
                        const savedSearch = userId !== undefined ? savedSearches[String(userId)] : undefined;

                        return ({
                            ...row,
                            [COLUMNS.SAVED_SEARCHES]: savedSearch ? [
                                savedSearch.savedSearchesCount,
                                savedSearch.alertsCount ? `(${savedSearch.alertsCount})` : null,
                            ].filter(Boolean).join(' ') : '',
                        });
                    });
                } else {
                    fetchingFailed = true;
                }
            }

            if ([
                COLUMNS.CREATOR,
                COLUMNS.CREATED_DATE,
                COLUMNS.LAST_MODIFIER,
                COLUMNS.LAST_MODIFIED_DATE,
            ].some(column => selectedColumns.includes(column))) {
                const usersAuditResponse = await apiDispatch(getUsersAudit({
                    requestBody: userIds,
                }));

                const usersAudit = getSuccessPayload(usersAuditResponse.payload);
                if (usersAudit) {
                    if (selectedColumns.includes(COLUMNS.CREATOR)) {
                        data = data.map(row => {
                            const email = row[COLUMNS.EMAIL];
                            const userId = email ? emailsToIdsMap[email] : undefined;
                            const created = userId !== undefined ? usersAudit[String(userId)]?.created : undefined;
                            const modifierInfo = created?.modifierInfo;

                            const fullName = modifierInfo ? [
                                modifierInfo.userFirstName,
                                modifierInfo.userLastName,
                            ].filter(Boolean).join(' ') || modifierInfo.userId?.toString() : '';

                            return ({
                                ...row,
                                [COLUMNS.CREATOR]: fullName,
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.CREATED_DATE)) {
                        data = data.map(row => {
                            const email = row[COLUMNS.EMAIL];
                            const userId = email ? emailsToIdsMap[email] : undefined;
                            const created = userId !== undefined ? usersAudit[String(userId)]?.created : undefined;

                            return ({
                                ...row,
                                [COLUMNS.CREATED_DATE]: FormattedDate.format({
                                    value: created?.eventTime,
                                    format: 'YYYY-MM-DD',
                                }) || '',
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.LAST_MODIFIER)) {
                        data = data.map(row => {
                            const email = row[COLUMNS.EMAIL];
                            const userId = email ? emailsToIdsMap[email] : undefined;
                            const updated = userId !== undefined ? usersAudit[String(userId)]?.lastUpdated : undefined;
                            const modifierInfo = updated?.modifierInfo;

                            const fullName = modifierInfo ? [
                                modifierInfo.userFirstName,
                                modifierInfo.userLastName,
                            ].filter(Boolean).join(' ') || modifierInfo.userId?.toString() : '';

                            return ({
                                ...row,
                                [COLUMNS.LAST_MODIFIER]: fullName,
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.LAST_MODIFIED_DATE)) {
                        data = data.map(row => {
                            const email = row[COLUMNS.EMAIL];
                            const userId = email ? emailsToIdsMap[email] : undefined;
                            const updated = userId !== undefined ? usersAudit[String(userId)]?.lastUpdated : undefined;

                            return ({
                                ...row,
                                [COLUMNS.LAST_MODIFIED_DATE]: FormattedDate.format({
                                    value: updated?.eventTime,
                                    format: 'YYYY-MM-DD',
                                }) || '',
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.LAST_MODIFICATION_REASON)) {
                        data = data.map(row => {
                            const email = row[COLUMNS.EMAIL];
                            const userId = email ? emailsToIdsMap[email] : undefined;
                            const updated = userId !== undefined ? usersAudit[String(userId)]?.lastUpdated : undefined;

                            return ({...row, [COLUMNS.LAST_MODIFICATION_REASON]: updated?.modifierReason || ''});
                        });
                    }
                } else {
                    fetchingFailed = true;
                }
            }

            if ([
                COLUMNS.SALESFORCE_ID,
                COLUMNS.MS_DYNAMICS_ID,
                COLUMNS.DEAL_CLOUD_ID,
                COLUMNS.HUBSPOT_ID,
            ].some(column => selectedColumns.includes(column))) {
                const usersAuditResponse = await apiDispatch(getUsersCRMIDsByIds({
                    requestBody: userIds,
                }));

                const userCrmSettings = getSuccessPayload(usersAuditResponse.payload);
                if (userCrmSettings) {
                    if (selectedColumns.includes(COLUMNS.SALESFORCE_ID)) {
                        data = data.map(row => {
                            const email = row[COLUMNS.EMAIL];
                            const userId = email ? emailsToIdsMap[email] : undefined;
                            const crmSettings = userId !== undefined ? userCrmSettings[String(userId)] : undefined;

                            return ({
                                ...row,
                                [COLUMNS.SALESFORCE_ID]: crmSettings?.SFDCId || '',
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.MS_DYNAMICS_ID)) {
                        data = data.map(row => {
                            const email = row[COLUMNS.EMAIL];
                            const userId = email ? emailsToIdsMap[email] : undefined;
                            const crmSettings = userId !== undefined ? userCrmSettings[String(userId)] : undefined;

                            return ({
                                ...row,
                                [COLUMNS.MS_DYNAMICS_ID]: crmSettings?.MSDynId || '',
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.DEAL_CLOUD_ID)) {
                        data = data.map(row => {
                            const email = row[COLUMNS.EMAIL];
                            const userId = email ? emailsToIdsMap[email] : undefined;
                            const crmSettings = userId !== undefined ? userCrmSettings[String(userId)] : undefined;

                            return ({
                                ...row,
                                [COLUMNS.DEAL_CLOUD_ID]: crmSettings?.dealCloudId || '',
                            });
                        });
                    }

                    if (selectedColumns.includes(COLUMNS.HUBSPOT_ID)) {
                        data = data.map(row => {
                            const email = row[COLUMNS.EMAIL];
                            const userId = email ? emailsToIdsMap[email] : undefined;
                            const crmSettings = userId !== undefined ? userCrmSettings[String(userId)] : undefined;

                            return ({
                                ...row,
                                [COLUMNS.HUBSPOT_ID]: crmSettings?.hubSpotId?.toString() || '',
                            });
                        });
                    }
                } else {
                    fetchingFailed = true;
                }
            }

            if ([
                ...TOTAL_LIMIT_COLUMNS,
                ...USED_LIMIT_COLUMNS,
            ].some(column => selectedColumns.includes(column))) {
                const limitsResponse = await apiDispatch(getUsersTotalLimits({
                    usersTotalLimitRequest: {
                        usersId: userIds,
                        totalLimitColumn: selectedColumns
                            .filter(column => TOTAL_LIMIT_COLUMNS.includes(column))
                            .map(columnValue => Object
                                .entries(COLUMNS)
                                .find(([, value]) => value === columnValue)?.[0] || '',
                            ),
                        usedLimitColumn: selectedColumns
                            .filter(column => USED_LIMIT_COLUMNS.includes(column))
                            .map(columnValue => Object
                                .entries(COLUMNS)
                                .find(([, value]) => value === columnValue)?.[0] || '',
                            ),
                    },
                }));

                const limitsMap = getSuccessPayload(limitsResponse.payload);
                if (limitsMap) {
                    data = data.map(row => {
                        const email = row[COLUMNS.EMAIL];
                        const userId = email ? emailsToIdsMap[email] : undefined;
                        const userLimits: UserSearchLimit[] = userId !== undefined
                            ? limitsMap[String(userId)] || []
                            : [];
                        const selectedLimitsColumns = selectedColumns
                            .filter(column => (
                                TOTAL_LIMIT_COLUMNS.includes(column) || USED_LIMIT_COLUMNS.includes(column)
                            ));

                        return {
                            ...row,
                            ...selectedLimitsColumns.reduce<Record<string, string>>((acc, curr) => {
                                const limit = userLimits.find(lim => getMappedLimitColumnId(curr) === lim.columnId);

                                if (!limit) {
                                    acc[curr] = '--';
                                    return acc;
                                }

                                if (limit.unlimited) {
                                    acc[curr] = 'Unlimited';
                                    return acc;
                                }

                                acc[curr] = String(limit.value);
                                return acc;
                            }, {}),
                        };
                    });
                } else {
                    fetchingFailed = true;
                }
            }
        } else {
            fetchingFailed = true;
        }

        if (!fetchingFailed) {
            return Promise.resolve({type: '', payload: {success: true, error: false, data}});
        } else {
            return Promise.resolve({type: '', payload: {
                success: false,
                error: true,
                data: {message: 'Failed to fetch data for the report'},
            }});
        }
    }, [
        apiDispatch,
        selectedColumns,
        productsReady,
        templateColumns,
        getColumnValue,
        getSortedEntityIds,
        userIds,
        cacheKeys.sortCriteria.direction,
        result,
        newsletterLabels,
    ]);

    return (
        <ChunkedDownloadTool
            ids={userIds}
            taskName={USER_DOWNLOAD_TASK_NAME}
            fileName={USER_DOWNLOAD_FILE_NAME}
            fileHeaders={fileHeaders}
            getData={getData}
            chunkSize={250}>
            {handler => children(handler)}
        </ChunkedDownloadTool>
    );
};
